import { FC, useState, useMemo } from 'react'
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  InputAdornment,
  Autocomplete,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Switch
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import { Person, PersonUpdateData } from '@services/contacts/contacts.models'
import { usePersonUpdate } from '@services/contacts/contacts.hooks'
import { NATIONALITY_CHOICES } from '@consts/nationalites'
import { useCountries, useSectors } from '@services/meta/meta.hooks'
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Language as LanguageIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material'
import { clientFormTranslations, Language, TranslationKey } from './clientForm.translations'

// Country codes for phone numbers
const PHONE_COUNTRY_CODES = [
  { code: '+48', name: 'Polska (+48)', country: 'PL' },
  { code: '+1', name: 'USA/Kanada (+1)', country: 'US' },
  { code: '+44', name: 'Wielka Brytania (+44)', country: 'GB' },
  { code: '+49', name: 'Niemcy (+49)', country: 'DE' },
  { code: '+33', name: 'Francja (+33)', country: 'FR' },
  { code: '+39', name: 'Włochy (+39)', country: 'IT' },
  { code: '+34', name: 'Hiszpania (+34)', country: 'ES' },
  { code: '+31', name: 'Holandia (+31)', country: 'NL' },
  { code: '+32', name: 'Belgia (+32)', country: 'BE' },
  { code: '+41', name: 'Szwajcaria (+41)', country: 'CH' },
  { code: '+43', name: 'Austria (+43)', country: 'AT' },
  { code: '+420', name: 'Czechy (+420)', country: 'CZ' },
  { code: '+421', name: 'Słowacja (+421)', country: 'SK' },
  { code: '+380', name: 'Ukraina (+380)', country: 'UA' },
  { code: '+7', name: 'Rosja (+7)', country: 'RU' },
  { code: '+370', name: 'Litwa (+370)', country: 'LT' },
  { code: '+371', name: 'Łotwa (+371)', country: 'LV' },
  { code: '+372', name: 'Estonia (+372)', country: 'EE' },
  { code: '+46', name: 'Szwecja (+46)', country: 'SE' },
  { code: '+47', name: 'Norwegia (+47)', country: 'NO' },
  { code: '+45', name: 'Dania (+45)', country: 'DK' },
  { code: '+358', name: 'Finlandia (+358)', country: 'FI' },
  { code: '+36', name: 'Węgry (+36)', country: 'HU' },
  { code: '+40', name: 'Rumunia (+40)', country: 'RO' },
  { code: '+359', name: 'Bułgaria (+359)', country: 'BG' },
  { code: '+30', name: 'Grecja (+30)', country: 'GR' },
  { code: '+351', name: 'Portugalia (+351)', country: 'PT' },
  { code: '+353', name: 'Irlandia (+353)', country: 'IE' },
  { code: '+385', name: 'Chorwacja (+385)', country: 'HR' },
  { code: '+386', name: 'Słowenia (+386)', country: 'SI' },
  { code: '+381', name: 'Serbia (+381)', country: 'RS' },
  { code: '+382', name: 'Czarnogóra (+382)', country: 'ME' },
  { code: '+387', name: 'Bośnia i Hercegowina (+387)', country: 'BA' },
  { code: '+389', name: 'Macedonia Północna (+389)', country: 'MK' },
  { code: '+355', name: 'Albania (+355)', country: 'AL' },
  { code: '+375', name: 'Białoruś (+375)', country: 'BY' },
  { code: '+373', name: 'Mołdawia (+373)', country: 'MD' },
  { code: '+90', name: 'Turcja (+90)', country: 'TR' },
  { code: '+91', name: 'Indie (+91)', country: 'IN' },
  { code: '+86', name: 'Chiny (+86)', country: 'CN' },
  { code: '+81', name: 'Japonia (+81)', country: 'JP' },
  { code: '+82', name: 'Korea Południowa (+82)', country: 'KR' },
  { code: '+61', name: 'Australia (+61)', country: 'AU' },
  { code: '+64', name: 'Nowa Zelandia (+64)', country: 'NZ' },
  { code: '+27', name: 'RPA (+27)', country: 'ZA' },
  { code: '+55', name: 'Brazylia (+55)', country: 'BR' },
  { code: '+52', name: 'Meksyk (+52)', country: 'MX' },
  { code: '+54', name: 'Argentyna (+54)', country: 'AR' },
  { code: '+56', name: 'Chile (+56)', country: 'CL' },
  { code: '+57', name: 'Kolumbia (+57)', country: 'CO' },
  { code: '+58', name: 'Wenezuela (+58)', country: 'VE' },
  { code: '+20', name: 'Egipt (+20)', country: 'EG' },
  { code: '+971', name: 'ZEA (+971)', country: 'AE' },
  { code: '+966', name: 'Arabia Saudyjska (+966)', country: 'SA' },
  { code: '+972', name: 'Izrael (+972)', country: 'IL' },
  { code: '+98', name: 'Iran (+98)', country: 'IR' },
  { code: '+92', name: 'Pakistan (+92)', country: 'PK' },
  { code: '+880', name: 'Bangladesz (+880)', country: 'BD' },
  { code: '+84', name: 'Wietnam (+84)', country: 'VN' },
  { code: '+66', name: 'Tajlandia (+66)', country: 'TH' },
  { code: '+60', name: 'Malezja (+60)', country: 'MY' },
  { code: '+65', name: 'Singapur (+65)', country: 'SG' },
  { code: '+62', name: 'Indonezja (+62)', country: 'ID' },
  { code: '+63', name: 'Filipiny (+63)', country: 'PH' }
]

// Language mappings - backend expects IDs
const LANGUAGE_CHOICES = [
  { id: 1, code: 'pl', name: 'Polski' },
  { id: 2, code: 'en', name: 'Angielski' },
  { id: 3, code: 'ru', name: 'Rosyjski' },
  { id: 4, code: 'ua', name: 'Ukraiński' }
]

// Employment form mappings - backend expects codes
const EMPLOYMENT_FORM_CHOICES = {
  pl: [
    { code: 1, name: 'Umowa zlecenie / dzieło' },
    { code: 2, name: 'Firma' },
    { code: 3, name: 'Umowa o pracę' },
    { code: 4, name: 'Jednoosobowa działalność gospodarcza' },
    { code: 5, name: 'Praca za granicą' },
    { code: 6, name: 'Brak stałego miejsca pracy' }
  ],
  en: [
    { code: 1, name: 'Contract of mandate / specific work' },
    { code: 2, name: 'Company' },
    { code: 3, name: 'Employment Contract' },
    { code: 4, name: 'Sole proprietorship' },
    { code: 5, name: 'Employment abroad' },
    { code: 6, name: 'No permanent place of work' }
  ]
}

interface ClientUpdateFormProps {
  person: Person
  onCancel: () => void
  onSave: (updatedPerson: Person) => void
}

// Funkcja walidująca IBAN zgodnie ze standardem ISO 13616 (mod-97)
const validateIBAN = (iban: string | undefined): boolean => {
  if (!iban) return true // pole opcjonalne

  // Usuń spacje i konwertuj na wielkie litery
  const cleanIban = iban.replace(/\s/g, '').toUpperCase()

  // Sprawdź format (2 litery + 2 cyfry + 1-30 znaków alfanumerycznych)
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(cleanIban)) {
    return false
  }

  // Sprawdź długość (15-34 znaki zgodnie ze standardem IBAN)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return false
  }

  // Przenieś pierwsze 4 znaki na koniec
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4)

  // Zamień litery na liczby (A=10, B=11, ..., Z=35)
  const numericString = rearranged
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      return code >= 65 && code <= 90 ? (code - 55).toString() : char
    })
    .join('')

  // Oblicz modulo 97 (algorytm weryfikacji IBAN)
  let remainder = numericString.slice(0, 2)
  for (let offset = 2; offset < numericString.length; offset += 7) {
    const fragment = remainder + numericString.substring(offset, offset + 7)
    remainder = (parseInt(fragment, 10) % 97).toString()
  }

  return parseInt(remainder, 10) === 1
}

const createValidationSchema = (t: (key: TranslationKey) => string, hasExistingEmail: boolean) =>
  Yup.object({
    firstName: Yup.string()
      .min(2, t('firstNameMinLength'))
      .max(50, t('firstNameMaxLength'))
      .required(t('firstNameRequiredValidation')),
    lastName: Yup.string()
      .min(2, t('lastNameMinLength'))
      .max(50, t('lastNameMaxLength'))
      .required(t('lastNameRequiredValidation')),
    email: hasExistingEmail
      ? Yup.string().email(t('emailInvalid'))
      : Yup.string().email(t('emailInvalid')).required(t('emailRequiredValidation')),
    mobile: Yup.string().matches(/^[\+]?[\d\s\-\(\)]+$/, t('mobileInvalid')),
    pesel: Yup.string().matches(/^\d{11}$/, t('peselInvalid')),
    nip: Yup.string().matches(/^\d{10}$/, t('nipInvalid')),
    bankAccountIban: Yup.string().test('is-valid-iban', t('ibanInvalid'), validateIBAN)
  })

const ClientUpdateForm: FC<ClientUpdateFormProps> = ({ person, onCancel, onSave }) => {
  const { enqueueSnackbar } = useSnackbar()
  const updatePersonMutation = usePersonUpdate()
  const [language, setLanguage] = useState<Language>('pl')
  const [shouldExitAfterSave, setShouldExitAfterSave] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Convert nationality choices to options format
  const nationalityOptions = Object.entries(NATIONALITY_CHOICES).map(([code, name]) => ({
    code,
    name
  }))

  // Fetch countries for passport issuing country field and sectors for employment sector
  const { data: countries = [] } = useCountries()
  const { data: sectors = [] } = useSectors()

  const t = (key: TranslationKey): string => {
    return clientFormTranslations[language][key]
  }

  // Handle save actions
  const handleSave = () => {
    setShouldExitAfterSave(false)
    formik.handleSubmit()
  }

  const handleSaveAndExit = () => {
    setShouldExitAfterSave(true)
    formik.handleSubmit()
  }

  const hasExistingEmail = !!(person.email && person.email.trim())
  const validationSchema = useMemo(
    () => createValidationSchema(t, hasExistingEmail),
    [language, hasExistingEmail]
  )

  // Helper function to copy main address to other address fields
  const copyMainAddress = (targetType: 'correspondence' | 'billing') => {
    const mainStreet = formik.values.addressStreet
    const mainCity = formik.values.addressCity
    const mainZipcode = formik.values.addressZipcode
    const mainCountry = formik.values.addressCountry

    if (mainStreet || mainCity || mainZipcode || mainCountry) {
      if (targetType === 'correspondence') {
        formik.setFieldValue('correspondenceAddressStreet', mainStreet)
        formik.setFieldValue('correspondenceAddressCity', mainCity)
        formik.setFieldValue('correspondenceAddressZipcode', mainZipcode)
        formik.setFieldValue('correspondenceAddressCountry', mainCountry)
      } else {
        formik.setFieldValue('billingAddressStreet', mainStreet)
        formik.setFieldValue('billingAddressCity', mainCity)
        formik.setFieldValue('billingAddressZipcode', mainZipcode)
        formik.setFieldValue('billingAddressCountry', mainCountry)
      }
      enqueueSnackbar(t('addressCopied'), { variant: 'success' })
    }
  }

  // Helper function to render sensitive data field with status indicator
  const renderSensitiveField = (
    fieldName: keyof PersonUpdateData,
    setField: keyof Person,
    label: string,
    type: 'text' | 'date' = 'text',
    multiline = false,
    rows = 1
  ) => {
    const isSet = person[setField as keyof Person] as boolean
    const hasData = isSet || false

    // For date fields, ensure proper formatting
    const fieldValue = formik.values[fieldName] || ''
    const displayValue = type === 'date' && fieldValue === '' ? '' : fieldValue

    return (
      <TextField
        fullWidth
        label={label}
        name={fieldName}
        type={type}
        multiline={multiline}
        rows={rows}
        value={displayValue}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
        helperText={formik.touched[fieldName] && formik.errors[fieldName]}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <Chip
                size='small'
                label={hasData ? t('dataSet') : t('dataNotSet')}
                color={hasData ? 'success' : 'default'}
                variant='outlined'
              />
            </InputAdornment>
          )
        }}
      />
    )
  }

  const formik = useFormik<PersonUpdateData>({
    initialValues: {
      firstName: person.firstName || '',
      lastName: person.lastName || '',
      email: person.email || '',
      mobile: person.mobile || '',
      mobileCountryCode: person.mobileCountryCode || '+48',
      company: person.company || '',
      billingContact: person.billingContact || false,
      sex: person.sex ?? undefined,
      // Sensitive fields - start empty for editing (masked data not sent from API)
      dateOfBirth: '',
      placeOfBirth: person.placeOfBirth || '',
      nationality: person.nationality || '',
      isForeigner: person.isForeigner || false,
      pesel: '',
      peselAssignmentDate: '',
      nip: '',
      businessName: person.businessName || '',
      idCard: '',
      passportId: '',
      passportIssuingCountry: person.passportIssuingCountry || undefined,
      passportIssuingCountryText: person.passportIssuingCountryText || '',
      foreignIdCard: person.foreignIdCard || '',
      foreignIdCardNation: person.foreignIdCardNation || '',
      residenceCardNumber: person.residenceCardNumber || '',
      residenceCardExpiryDate: '',
      incomeSource: person.incomeSource || '',
      industry: person.industry || '',
      bankAccountIban: '',
      bankAccountForeign: person.bankAccountForeign || '',
      bankAccountType: person.bankAccountType || 'iban',
      bankAccountSwift: person.bankAccountSwift || '',
      bankName: person.bankName || '',
      bankAccountData: person.bankAccountData || '',
      hasForeignBankAccount: person.hasForeignBankAccount || false,
      hasAccount: person.hasAccount || false,
      isCompanyRepresentative: person.isCompanyRepresentative || false,
      companyRepresentativeFunction: person.companyRepresentativeFunction || '',
      language: person.language
        ? typeof person.language === 'number'
          ? person.language
          : LANGUAGE_CHOICES.find((lang) => lang.code === person.language)?.id || ''
        : '',
      sector: person.sector || null,
      employmentForm: person.employmentForm || null,
      description: person.description || '',
      // Address fields - main address
      addressStreet: person.addressStreet || '',
      addressCity: person.addressCity || '',
      addressZipcode: person.addressZipcode || '',
      addressCountry: person.addressCountry || undefined,
      // Correspondence address
      correspondenceAddressStreet: person.correspondenceAddressStreet || '',
      correspondenceAddressCity: person.correspondenceAddressCity || '',
      correspondenceAddressZipcode: person.correspondenceAddressZipcode || '',
      correspondenceAddressCountry: person.correspondenceAddressCountry || undefined,
      // Billing address
      billingAddressStreet: person.billingAddressStreet || '',
      billingAddressCity: person.billingAddressCity || '',
      billingAddressZipcode: person.billingAddressZipcode || '',
      billingAddressCountry: person.billingAddressCountry || undefined
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // Filter out empty date fields to avoid backend validation errors
        const cleanedValues = Object.fromEntries(
          Object.entries(values).filter(([key, value]) => {
            // Remove empty date fields
            if (
              (key === 'dateOfBirth' ||
                key === 'peselAssignmentDate' ||
                key === 'residenceCardExpiryDate') &&
              (value === '' || value === null || value === undefined)
            ) {
              return false
            }
            // Remove empty string fields (but keep boolean false values)
            if (typeof value === 'string' && value.trim() === '') {
              return false
            }
            // Keep number values (including country IDs)
            if (typeof value === 'number') {
              return true
            }
            // Keep null values for countries
            if (value === null && (key === 'nationality' || key === 'passportIssuingCountry')) {
              return false
            }
            return true
          })
        ) as PersonUpdateData

        // Nationality is now handled via local NATIONALITY_CHOICES as string codes
        if (
          cleanedValues.passportIssuingCountry !== undefined &&
          cleanedValues.passportIssuingCountry !== null
        ) {
          cleanedValues.passportIssuingCountry =
            typeof cleanedValues.passportIssuingCountry === 'number'
              ? cleanedValues.passportIssuingCountry
              : parseInt(String(cleanedValues.passportIssuingCountry), 10)
        }

        // Convert language ID to number (backend expects pk value)
        if (cleanedValues.language !== undefined && cleanedValues.language !== '') {
          cleanedValues.language =
            typeof cleanedValues.language === 'number'
              ? cleanedValues.language
              : parseInt(String(cleanedValues.language), 10)
        }

        const updatedPerson = await updatePersonMutation.mutateAsync({
          id: person.id,
          data: cleanedValues
        })
        enqueueSnackbar(t('updateSuccess'), { variant: 'success' })

        // Call onSave only if user wants to exit
        if (shouldExitAfterSave) {
          onSave(updatedPerson)
        } else {
          // Reset the flag after successful save
          setShouldExitAfterSave(false)
        }
      } catch (error: any) {
        console.error('Error updating person:', error)

        // Show backend error message if available, otherwise show generic error
        const errorMessage = error?.response?.data
          ? `${t('updateError')}: ${JSON.stringify(error.response.data)}`
          : `${t('updateError')}: ${(error as Error).message}`

        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    }
  })

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {isMobile ? (
          // Mobile Layout - Stack vertically
          <>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={onCancel}
                variant='outlined'
                size='small'
              >
                {t('backButton')}
              </Button>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ ml: 'auto' }}>
                <LanguageIcon color='action' fontSize='small' />
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={(_, newLanguage) => {
                    if (newLanguage) setLanguage(newLanguage)
                  }}
                  size='small'
                >
                  <ToggleButton value='pl'>PL</ToggleButton>
                  <ToggleButton value='en'>EN</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            <Typography variant='h6' sx={{ fontWeight: 'medium' }}>
              {t('editingClient')}: {person.firstName} {person.lastName}
            </Typography>
          </>
        ) : (
          // Desktop Layout - Side by side
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
            <Stack direction='row' spacing={2} alignItems='center'>
              <Button startIcon={<ArrowBackIcon />} onClick={onCancel} variant='outlined'>
                {t('backButton')}
              </Button>
              <Typography variant='h5'>
                {t('editingClient')}: {person.firstName} {person.lastName}
              </Typography>
            </Stack>

            <Stack direction='row' spacing={2} alignItems='center'>
              <LanguageIcon color='action' />
              <ToggleButtonGroup
                value={language}
                exclusive
                onChange={(_, newLanguage) => {
                  if (newLanguage) setLanguage(newLanguage)
                }}
                size='small'
              >
                <ToggleButton value='pl'>PL</ToggleButton>
                <ToggleButton value='en'>EN</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        )}
      </Stack>

      <form onSubmit={(e) => e.preventDefault()}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('basicInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('firstNameRequired')}
                    name='firstName'
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('lastNameRequired')}
                    name='lastName'
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('emailRequired')}
                    name='email'
                    type='email'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={4} md={3}>
                      <TextField
                        fullWidth
                        select
                        label={t('mobileCountryCode')}
                        name='mobileCountryCode'
                        value={formik.values.mobileCountryCode}
                        onChange={formik.handleChange}
                      >
                        {PHONE_COUNTRY_CODES.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={8} md={9}>
                      <TextField
                        fullWidth
                        label={t('mobile')}
                        name='mobile'
                        value={formik.values.mobile}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                        helperText={formik.touched.mobile && formik.errors.mobile}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('company')}
                    name='company'
                    value={formik.values.company}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Personal Details */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('personalDetails')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label={t('gender')}
                    name='sex'
                    value={formik.values.sex ?? ''}
                    onChange={(e) => {
                      const value = e.target.value
                      formik.setFieldValue('sex', value === '' ? undefined : Number(value))
                    }}
                  >
                    <MenuItem value={0}>{t('male')}</MenuItem>
                    <MenuItem value={1}>{t('female')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  {renderSensitiveField('dateOfBirth', 'dateOfBirthSet', t('dateOfBirth'), 'date')}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('placeOfBirth')}
                    name='placeOfBirth'
                    value={formik.values.placeOfBirth}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={nationalityOptions}
                    getOptionLabel={(option) => option.name}
                    value={
                      nationalityOptions.find(
                        (option) =>
                          option.code === formik.values.nationality ||
                          option.name === formik.values.nationality
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      // Send nationality code to API
                      formik.setFieldValue('nationality', newValue?.code || null)
                      // Auto-mark as foreigner if nationality is not Polish
                      formik.setFieldValue('isForeigner', newValue?.code !== 'PL')
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('nationality')}
                        name='nationality'
                        error={formik.touched.nationality && Boolean(formik.errors.nationality)}
                        helperText={formik.touched.nationality && formik.errors.nationality}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.isForeigner || false}
                        onChange={(e) => formik.setFieldValue('isForeigner', e.target.checked)}
                      />
                    }
                    label={t('isForeigner')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSensitiveField('pesel', 'peselSet', t('pesel'))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      fullWidth
                      label={t('peselAssignmentDate')}
                      name='peselAssignmentDate'
                      type='date'
                      value={formik.values.peselAssignmentDate || ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.peselAssignmentDate &&
                        Boolean(formik.errors.peselAssignmentDate)
                      }
                      helperText={
                        formik.touched.peselAssignmentDate && formik.errors.peselAssignmentDate
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {t('peselAssignmentNote')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSensitiveField('nip', 'nipSet', t('nip'))}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('businessName')}
                    name='businessName'
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Document Information */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('documents')}
              </Typography>

              {/* Note about required documents */}
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 3, fontStyle: 'italic' }}
              >
                {t('documentsNote')}
              </Typography>

              <Grid container spacing={2}>
                {/* Always show ID Card and Passport for all users */}
                <Grid item xs={12} md={6}>
                  {renderSensitiveField('idCard', 'idCardSet', t('idCard'))}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderSensitiveField('passportId', 'passportIdSet', t('passportId'))}
                </Grid>

                {/* Show passport country only if passport ID is filled or user is foreigner */}
                {(formik.values.passportId || formik.values.isForeigner) && (
                  <Grid item xs={12}>
                    <Autocomplete
                      fullWidth
                      freeSolo
                      options={countries}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option
                        }
                        return option.name
                      }}
                      value={
                        countries.find(
                          (country) => country.id === formik.values.passportIssuingCountry
                        ) ||
                        formik.values.passportIssuingCountryText ||
                        null
                      }
                      onChange={(_, newValue) => {
                        if (typeof newValue === 'string') {
                          // Manual text entry
                          formik.setFieldValue('passportIssuingCountry', null)
                          formik.setFieldValue('passportIssuingCountryText', newValue)
                        } else if (newValue) {
                          // Selected from dropdown
                          formik.setFieldValue('passportIssuingCountry', newValue.id)
                          formik.setFieldValue('passportIssuingCountryText', '')
                        } else {
                          // Cleared
                          formik.setFieldValue('passportIssuingCountry', null)
                          formik.setFieldValue('passportIssuingCountryText', '')
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        // Handle manual typing
                        formik.setFieldValue('passportIssuingCountryText', newInputValue)
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('passportCountry')}
                          name='passportIssuingCountry'
                          error={
                            formik.touched.passportIssuingCountry &&
                            Boolean(formik.errors.passportIssuingCountry)
                          }
                          helperText={
                            formik.touched.passportIssuingCountry &&
                            formik.errors.passportIssuingCountry
                          }
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Show foreign documents only for foreigners */}
                {formik.values.isForeigner && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('foreignIdCard')}
                        name='foreignIdCard'
                        value={formik.values.foreignIdCard}
                        onChange={formik.handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('residenceCardNumber')}
                        name='residenceCardNumber'
                        value={formik.values.residenceCardNumber || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.residenceCardNumber &&
                          Boolean(formik.errors.residenceCardNumber)
                        }
                        helperText={
                          formik.touched.residenceCardNumber && formik.errors.residenceCardNumber
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderSensitiveField(
                        'residenceCardExpiryDate',
                        'residenceCardExpiryDateSet',
                        t('residenceCardExpiryDate'),
                        'date'
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('addressInfo')}
              </Typography>

              {/* Main Address */}
              <Typography variant='subtitle1' gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                {t('mainAddress')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('street')}
                    name='addressStreet'
                    value={formik.values.addressStreet}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('city')}
                    name='addressCity'
                    value={formik.values.addressCity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('zipcode')}
                    name='addressZipcode'
                    value={formik.values.addressZipcode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    value={
                      countries.find((country) => country.id === formik.values.addressCountry) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      formik.setFieldValue('addressCountry', newValue?.id || null)
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('country')} name='addressCountry' />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Correspondence Address */}
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 3, mb: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 'medium' }}>
                  {t('correspondenceAddress')}
                </Typography>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyMainAddress('correspondence')}
                  sx={{ ml: 'auto' }}
                >
                  {t('copyFromMain')}
                </Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('street')}
                    name='correspondenceAddressStreet'
                    value={formik.values.correspondenceAddressStreet}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('city')}
                    name='correspondenceAddressCity'
                    value={formik.values.correspondenceAddressCity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('zipcode')}
                    name='correspondenceAddressZipcode'
                    value={formik.values.correspondenceAddressZipcode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    value={
                      countries.find(
                        (country) => country.id === formik.values.correspondenceAddressCountry
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      formik.setFieldValue('correspondenceAddressCountry', newValue?.id || null)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('country')}
                        name='correspondenceAddressCountry'
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Billing Address */}
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 3, mb: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 'medium' }}>
                  {t('billingAddress')}
                </Typography>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyMainAddress('billing')}
                  sx={{ ml: 'auto' }}
                >
                  {t('copyFromMain')}
                </Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('street')}
                    name='billingAddressStreet'
                    value={formik.values.billingAddressStreet}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('city')}
                    name='billingAddressCity'
                    value={formik.values.billingAddressCity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label={t('zipcode')}
                    name='billingAddressZipcode'
                    value={formik.values.billingAddressZipcode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    value={
                      countries.find(
                        (country) => country.id === formik.values.billingAddressCountry
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      formik.setFieldValue('billingAddressCountry', newValue?.id || null)
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('country')} name='billingAddressCountry' />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Bank Information */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('bankInfo')}
              </Typography>
              <Grid container spacing={2}>
                {/* Account Type Selector */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label={t('accountType')}
                    name='bankAccountType'
                    value={formik.values.bankAccountType}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value='iban'>{t('accountTypeIban')}</MenuItem>
                    <MenuItem value='foreign'>{t('accountTypeForeign')}</MenuItem>
                  </TextField>
                </Grid>

                {/* IBAN Field - only for IBAN type */}
                {formik.values.bankAccountType === 'iban' && (
                  <Grid item xs={12}>
                    {renderSensitiveField('bankAccountIban', 'bankAccountIbanSet', t('iban'))}
                  </Grid>
                )}

                {/* Foreign Account Number - only for foreign type */}
                {formik.values.bankAccountType === 'foreign' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('foreignAccountNumber')}
                      name='bankAccountForeign'
                      value={formik.values.bankAccountForeign}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                )}

                {/* SWIFT Code - only for foreign accounts */}
                {formik.values.bankAccountType === 'foreign' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('swift')}
                      name='bankAccountSwift'
                      value={formik.values.bankAccountSwift}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                )}

                {/* Bank Name - always visible */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('bankName')}
                    name='bankName'
                    value={formik.values.bankName}
                    onChange={formik.handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                {t('additionalInfo')}
              </Typography>
              <Grid container spacing={2}>
                {/* Description - TEMPORARILY HIDDEN */}
                {/* <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('description')}
                    name='description'
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </Grid> */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('language')}
                    name='language'
                    value={formik.values.language || ''}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value=''></MenuItem>
                    {LANGUAGE_CHOICES.map((lang) => (
                      <MenuItem key={lang.id} value={lang.id}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={sectors}
                    getOptionLabel={(option) => option.name}
                    value={sectors.find((sector) => sector.id === formik.values.sector) || null}
                    onChange={(_, newValue) => {
                      formik.setFieldValue('sector', newValue?.id || null)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('employmentSector')}
                        name='sector'
                        error={formik.touched.sector && Boolean(formik.errors.sector)}
                        helperText={formik.touched.sector && formik.errors.sector}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={EMPLOYMENT_FORM_CHOICES[language]}
                    getOptionLabel={(option) => option.name}
                    value={
                      EMPLOYMENT_FORM_CHOICES[language].find(
                        (form) => form.code === formik.values.employmentForm
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      formik.setFieldValue('employmentForm', newValue?.code || null)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('employmentForm')}
                        name='employmentForm'
                        error={
                          formik.touched.employmentForm && Boolean(formik.errors.employmentForm)
                        }
                        helperText={formik.touched.employmentForm && formik.errors.employmentForm}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: isMobile ? 'stretch' : 'flex-end',
              flexDirection: isMobile ? 'column' : 'row'
            }}
          >
            <Button variant='outlined' onClick={onCancel} fullWidth={isMobile}>
              {t('cancelButton')}
            </Button>
            <Button
              variant='outlined'
              startIcon={<SaveIcon />}
              disabled={updatePersonMutation.isPending}
              onClick={handleSave}
              fullWidth={isMobile}
            >
              {updatePersonMutation.isPending && !shouldExitAfterSave
                ? t('saveButtonSaving')
                : t('saveButton')}
            </Button>
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              disabled={updatePersonMutation.isPending}
              onClick={handleSaveAndExit}
              fullWidth={isMobile}
            >
              {updatePersonMutation.isPending && shouldExitAfterSave
                ? t('saveAndExitButtonSaving')
                : t('saveAndExitButton')}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  )
}

export default ClientUpdateForm
